import { memo, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { SelectedThreadContext } from '@/App';
import { SidebarContext } from '@/contexts/SidebarContext';
import { SidebarChatThreads } from './SidebarChatThreads';
import { logger } from '@/lib/logger';

interface Session {
  id: string;
  title: string;
  created_at: string;
}

interface SidebarAskJDSProps {
  sessions: Session[];
  currentSession: string | null;
  isDesktopExpanded: boolean;
  setActiveTab: (tab: string | null) => void;
  onDeleteThread: (id: string) => void;
  onRenameThread: (id: string, newTitle: string) => void;
  onDesktopExpandedChange: (expanded: boolean) => void;
  onContextMenuOpenChange: (open: boolean) => void;
}

export const SidebarAskJDS = memo(function SidebarAskJDS({
  sessions,
  currentSession,
  isDesktopExpanded,
  setActiveTab,
  onDeleteThread,
  onRenameThread,
  onDesktopExpandedChange,
  onContextMenuOpenChange,
}: SidebarAskJDSProps) {
  const [threadsCollapsed, setThreadsCollapsed] = usePersistedState(
    'sidebar-threads-collapsed',
    false
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { setSelectedThreadId } = useContext(SelectedThreadContext);
  const { setIsExpanded, isMobile } = useContext(SidebarContext);

  const isInChat = location.pathname.startsWith('/chat');

  const handleAskJDSClick = useCallback(() => {
    logger.debug('Sidebar: Ask JDS clicked');

    // Special handling: go directly to most recent thread
    if (sessions.length > 0) {
      const mostRecentThread = sessions[0];
      setSelectedThreadId(mostRecentThread.id);
      setActiveTab(mostRecentThread.id);
      navigate(`/chat/${mostRecentThread.id}`, {
        replace: true,
        state: { fromSidebar: true },
      });
    } else {
      navigate('/chat', { replace: false, state: { fromSidebar: true } });
    }

    if (isMobile) {
      setIsExpanded(false);
      onDesktopExpandedChange(false);
    }
  }, [
    sessions,
    setSelectedThreadId,
    setActiveTab,
    navigate,
    isMobile,
    setIsExpanded,
    onDesktopExpandedChange,
  ]);

  return (
    <>
      {/* Ask JDS button with separate toggle area */}
      <div className="flex items-center gap-1">
        <Button
          variant={isInChat ? 'default' : 'ghost'}
          className={cn(
            'flex-1 flex items-center gap-2 transition-all min-h-[44px] md:h-9',
            isDesktopExpanded ? 'justify-start px-3' : 'justify-center px-0',
            isInChat && 'bg-[#F37022] hover:bg-[#E36012]',
            !isInChat && 'dark:text-gray-200 dark:hover:bg-gray-700',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
          )}
          onClick={handleAskJDSClick}
        >
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span
            className={cn(
              'transition-opacity duration-300 text-sm',
              isDesktopExpanded
                ? 'opacity-100'
                : 'opacity-0 absolute overflow-hidden w-0'
            )}
          >
            Ask JDS
          </span>
        </Button>
        {isDesktopExpanded && (
          <Button
            variant={isInChat ? 'default' : 'ghost'}
            size="icon"
            className={cn(
              'h-11 w-11 md:h-9 md:w-9 transition-all',
              isInChat && 'bg-[#F37022] hover:bg-[#E36012]',
              !isInChat && 'dark:text-gray-200 dark:hover:bg-gray-700',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1'
            )}
            aria-expanded={!threadsCollapsed}
            aria-label={threadsCollapsed ? 'Expand chat threads' : 'Collapse chat threads'}
            onClick={(e) => {
              e.stopPropagation();
              setThreadsCollapsed(!threadsCollapsed);
            }}
          >
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                threadsCollapsed && '-rotate-90'
              )}
            />
          </Button>
        )}
      </div>

      {/* Thread list — fills available space, scrolls internally */}
      {!threadsCollapsed && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <SidebarChatThreads
            sessions={sessions}
            currentSession={currentSession}
            isDesktopExpanded={isDesktopExpanded}
            setActiveTab={setActiveTab}
            onDeleteThread={onDeleteThread}
            onRenameThread={onRenameThread}
            onDesktopExpandedChange={onDesktopExpandedChange}
            onContextMenuOpenChange={onContextMenuOpenChange}
          />
        </div>
      )}
    </>
  );
});
